// Home page card surfacing the user's last-played track + position.
// - Renders only when a saved state exists.
// - "Resume" plays the track from the saved position (does NOT auto-play
//   on mount — user must tap).
// - "Clear" removes the state both locally and (when authed) on the backend.
// - If the saved track has been deleted/unapproved server-side, the loader
//   returns null and the card silently hides.

import React, { useEffect, useState, useCallback } from "react";
import { Play, X, Clock } from "lucide-react";
import { coverUrl, BACKEND_URL } from "../lib/api";
import { loadContinueState, clearContinueState } from "../lib/continueListening";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";

function formatPosition(s) {
  if (!Number.isFinite(s) || s <= 0) return "0:00";
  const mm = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return d.toLocaleDateString();
}

export default function ContinueListeningCard() {
  const { user } = useAuth();
  const { playTrackAt } = usePlayer();
  const [state, setState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const isAuthed = !!user;

  const reload = useCallback(async () => {
    const s = await loadContinueState({ isAuthed });
    setState(s);
    setLoaded(true);
  }, [isAuthed]);

  useEffect(() => { reload(); }, [reload]);

  const onResume = useCallback(() => {
    if (!state?.track) return;
    playTrackAt(state.track, state.position_seconds || 0);
  }, [state, playTrackAt]);

  const onClear = useCallback(async () => {
    await clearContinueState({ isAuthed });
    setState(null);
  }, [isAuthed]);

  if (!loaded || !state || !state.track) return null;

  const t = state.track;
  // Cover URL — `coverUrl` returns null when has_cover is false; fall back to backend cover endpoint when local snapshot lacks it.
  const cover = coverUrl(t) || (t.has_cover ? `${BACKEND_URL}/api/tracks/${t.id}/cover` : null);
  const stationLabel = state.station_name || (state.mode === "radio" ? "Yah-Wave Live" : null);

  return (
    <section
      data-testid="continue-listening-card"
      className="card-surface relative overflow-hidden bg-gradient-to-br from-[#1a1612] via-[#0E1014] to-[#0a0a14] p-4 md:p-5"
    >
      <div className="flex items-center gap-4 md:gap-5">
        <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border border-[#22262E] bg-[#111317] flex-shrink-0">
          {cover ? (
            <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Clock className="w-7 h-7 text-[#C5A059]/60" strokeWidth={1.5} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[#C5A059] mb-1 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Continue listening
          </div>
          <div className="text-[#FDFBF7] font-serif text-lg md:text-xl truncate" data-testid="continue-track-title">
            {t.title}
          </div>
          <div className="text-[#A1A4AB] text-sm truncate">
            {t.artist}
            {stationLabel && (
              <>
                <span className="mx-1.5 text-[#6C707A]">·</span>
                <span className="text-[#C5A059]/80">{stationLabel}</span>
              </>
            )}
            <span className="mx-1.5 text-[#6C707A]">·</span>
            <span>Picked up at {formatPosition(state.position_seconds)}</span>
            {state.updated_at && (
              <>
                <span className="mx-1.5 text-[#6C707A]">·</span>
                <span className="text-[#6C707A]">{relativeTime(state.updated_at)}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onResume}
            data-testid="continue-resume-btn"
            className="btn-primary !px-4 !py-2 text-sm"
            aria-label="Resume listening"
          >
            <Play className="w-4 h-4" strokeWidth={2} /> Resume
          </button>
          <button
            onClick={onClear}
            data-testid="continue-clear-btn"
            title="Clear continue listening"
            aria-label="Clear continue listening"
            className="w-9 h-9 rounded-full border border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7] hover:border-[#C5A059]/40 transition-colors flex items-center justify-center"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
}
